import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { useFocusTrap } from '@pazportal/ui';
import type { PropertyVisit } from '../lib/propertyCatalog';
import { useViewAs } from '../contexts/ViewAsContext';
import { useI18n } from '../i18n/I18nProvider';
import { useVisitPlans, visitPlansToCalendarTrips } from '../hooks/useVisitPlans';
import {
  catalogToCalendarTrips,
  fourMonthStarts,
  formatTripWindow,
  initialAnchor,
  liveToCalendarTrips,
  mergeCalendarTrips,
  monthCells,
  parseISODate,
  rangeLabel,
  shiftAnchor,
  todayISO,
  toISODate,
  tripsInRange,
  tripsOnDate,
  visibleRange,
  weekCells,
  WEEKDAY_LABELS,
  type CalendarRange,
  type CalendarTrip,
  type LiveTripLike,
} from '../lib/tripCalendar';

const VIEW_STORAGE_KEY = 'pazportal:trip-calendar-view';
const MODE_STORAGE_KEY = 'pazportal:trip-calendar-mode';

type DisplayMode = 'calendar' | 'list';

interface LocationOption {
  id: string;
  name: string;
}

interface OnsiteTripCalendarProps {
  /** Recorded on-site visits for this property, from property_visit_history. */
  visits?: PropertyVisit[] | null;
  /** Caption operations wrote above the visit history. */
  visitNote?: string | null;
  liveTrips?: LiveTripLike[] | null;
  locations?: LocationOption[];
  /** Customer the "Schedule visit" form writes to. Defaults to the active account. */
  customerId?: string | null;
}

function readStoredView(): CalendarRange {
  if (typeof window === 'undefined') return 'fourMonth';
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  if (stored === 'week' || stored === 'month' || stored === 'fourMonth') return stored;
  return 'fourMonth';
}

function readStoredMode(): DisplayMode {
  if (typeof window === 'undefined') return 'calendar';
  return window.localStorage.getItem(MODE_STORAGE_KEY) === 'list' ? 'list' : 'calendar';
}

function chipClass(trip: CalendarTrip): string {
  if (trip.source === 'planned' || trip.status === 'scheduled') {
    return 'bg-primary/15 text-primary';
  }
  if (trip.status === 'in_progress') {
    return 'bg-success/15 text-success';
  }
  return 'bg-surface-3 text-text-muted';
}

/** Dot form of chipClass, for month cells too narrow to carry a label. */
function dotClass(trip: CalendarTrip): string {
  if (trip.source === 'planned' || trip.status === 'scheduled') return 'bg-primary';
  if (trip.status === 'in_progress') return 'bg-success';
  return 'bg-text-faint';
}

function sourceLabel(trip: CalendarTrip, planned: string, completed: string, scheduled: string, onSite: string): string {
  if (trip.source === 'planned') return planned;
  if (trip.status === 'in_progress') return onSite;
  if (trip.status === 'completed') return completed;
  return scheduled;
}

export function OnsiteTripCalendar({
  visits,
  visitNote,
  liveTrips,
  locations = [],
  customerId,
}: OnsiteTripCalendarProps) {
  const { t } = useI18n();
  const { isStaff, viewAsUserId } = useViewAs();
  // Staff viewing the portal as a customer see exactly what the customer
  // sees but must not write on their behalf (same rule as tickets).
  const readOnly = isStaff && !!viewAsUserId;
  const { plans, create, remove } = useVisitPlans(customerId);
  const [view, setView] = useState<CalendarRange>(readStoredView);
  const [mode, setMode] = useState<DisplayMode>(readStoredMode);
  const [anchor, setAnchor] = useState(() => new Date());
  const didInitAnchor = useRef(false);
  const [selectedDate, setSelectedDate] = useState(() => todayISO());
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const formId = useId();
  const dialogTitleRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState({
    title: '',
    notes: '',
    start_date: todayISO(),
    end_date: todayISO(),
    location_id: locations[0]?.id ?? '',
  });

  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  // Dialog keyboard contract, shared so every dialog behaves the same: focus
  // lands on the first field, Tab stays inside, Escape closes, focus returns.
  useFocusTrap(dialogRef, showForm, {
    onClose: () => setShowForm(false),
    initialFocusRef: dialogTitleRef,
  });

  useEffect(() => {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  const trips = useMemo(
    () => mergeCalendarTrips(
      catalogToCalendarTrips(visits),
      liveToCalendarTrips(liveTrips),
      visitPlansToCalendarTrips(plans),
    ),
    [liveTrips, plans, visits],
  );

  useEffect(() => {
    if (didInitAnchor.current || trips.length === 0) return;
    didInitAnchor.current = true;
    const next = initialAnchor(trips);
    setAnchor(next);
    setSelectedDate(toISODate(next));
  }, [trips]);

  const windowRange = visibleRange(anchor, view);
  const tripsThisWindow = tripsInRange(trips, windowRange.start, windowRange.end);
  const today = todayISO();
  const selectedTrips = tripsOnDate(trips, selectedDate);
  const upcoming = trips.filter((trip) => trip.end >= today);
  const listTrips = [...trips].sort((a, b) => b.start.localeCompare(a.start) || a.title.localeCompare(b.title));

  function openAdd(date = selectedDate >= today ? selectedDate : today) {
    if (readOnly) return;
    setForm({
      title: '',
      notes: '',
      start_date: date,
      end_date: date,
      location_id: locations.length === 1 ? locations[0].id : form.location_id,
    });
    setFormError(null);
    setShowForm(true);
  }

  function onSelectDay(iso: string) {
    setSelectedDate(iso);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form.title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (form.end_date < form.start_date) {
      setFormError('End date must be on or after the start date.');
      return;
    }
    try {
      await create.mutateAsync({
        title: form.title,
        notes: form.notes,
        start_date: form.start_date,
        end_date: form.end_date,
        location_id: form.location_id || null,
      });
      setShowForm(false);
      setSelectedDate(form.start_date);
      setAnchor(parseISODate(form.start_date));
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not save this visit.');
    }
  }

  const viewButtons: { id: CalendarRange; label: string }[] = [
    { id: 'week', label: t('property.trips.week') },
    { id: 'month', label: t('property.trips.month') },
    { id: 'fourMonth', label: t('property.trips.four_month') },
  ];

  return (
    <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-sm font-medium text-text-muted">
            {t('property.trips.title')} ({trips.length})
          </h3>
          <p className="mt-0.5 text-xs text-text-faint">
            {t('property.trips.subtitle')}
            {upcoming.length > 0 ? ` · ${upcoming.length} upcoming` : ''}
          </p>
          {visitNote && (
            <p className="mt-1 text-xs leading-relaxed text-text-muted">{visitNote}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            {(['calendar', 'list'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMode(option)}
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  mode === option ? 'bg-surface-3 text-text' : 'text-text-muted hover:text-text'
                }`}
              >
                {option === 'calendar' ? t('property.trips.calendar') : t('property.trips.list')}
              </button>
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => openAdd()}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              {t('property.trips.add')}
            </button>
          )}
        </div>
      </div>

      {mode === 'calendar' && (
        <>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setAnchor((current) => shiftAnchor(current, view, -1))}
                className="rounded-md border border-border px-2 py-1 text-sm text-text-muted hover:bg-surface-strong"
                aria-label={t('calendar.previous')}
              >
                ‹
              </button>
              <p className="text-sm font-medium text-text sm:min-w-[10rem]">{rangeLabel(anchor, view)}</p>
              <button
                type="button"
                onClick={() => setAnchor((current) => shiftAnchor(current, view, 1))}
                className="rounded-md border border-border px-2 py-1 text-sm text-text-muted hover:bg-surface-strong"
                aria-label={t('calendar.next')}
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setAnchor(now);
                  setSelectedDate(todayISO(now));
                }}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-text-muted hover:bg-surface-strong"
              >
                {t('property.trips.today')}
              </button>
            </div>
            <div className="flex rounded-md border border-border p-0.5">
              {viewButtons.map((button) => (
                <button
                  key={button.id}
                  type="button"
                  onClick={() => setView(button.id)}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${
                    view === button.id ? 'bg-surface-3 text-text' : 'text-text-muted hover:text-text'
                  }`}
                >
                  {button.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-2 text-xs text-text-faint">
            {tripsThisWindow.length === 1
              ? t('calendar.visits_in_window_one')
              : t('calendar.visits_in_window', { n: tripsThisWindow.length })}
          </p>

          {view === 'week' && (
            <WeekGrid
              anchor={anchor}
              trips={trips}
              selectedDate={selectedDate}
              today={today}
              onSelect={onSelectDay}
              onAddFuture={(iso) => openAdd(iso)}
            />
          )}
          {view === 'month' && (
            <MonthGrid
              year={anchor.getFullYear()}
              month={anchor.getMonth()}
              trips={trips}
              selectedDate={selectedDate}
              today={today}
              compact={false}
              onSelect={onSelectDay}
              onAddFuture={(iso) => openAdd(iso)}
            />
          )}
          {view === 'fourMonth' && (
            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
              {fourMonthStarts(anchor).map((monthStart) => (
                <div key={toISODate(monthStart)} className="rounded-md border border-border/70 p-2 sm:p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
                    {monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                  <MonthGrid
                    year={monthStart.getFullYear()}
                    month={monthStart.getMonth()}
                    trips={trips}
                    selectedDate={selectedDate}
                    today={today}
                    compact
                    onSelect={onSelectDay}
                    onAddFuture={(iso) => openAdd(iso)}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 rounded-md border border-border bg-surface-strong/40 px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
                {formatTripWindow(selectedDate, selectedDate)}
              </p>
              {selectedDate >= today && !readOnly && (
                <button
                  type="button"
                  onClick={() => openAdd(selectedDate)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {t('property.trips.add')}
                </button>
              )}
            </div>
            {selectedTrips.length === 0 ? (
              <p className="mt-2 text-sm text-text-faint">{t('property.trips.empty_day')}</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {selectedTrips.map((trip) => (
                  <TripDetailRow
                    key={trip.id}
                    trip={trip}
                    plannedLabel={t('property.trips.planned')}
                    completedLabel={t('property.trips.completed')}
                    scheduledLabel={t('property.trips.scheduled')}
                    onSiteLabel={t('property.trips.on_site')}
                    onRemove={trip.source === 'planned' && !readOnly ? () => void remove.mutate(trip.id.replace(/^planned:/, '')) : undefined}
                    removeLabel={t('calendar.remove')}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {mode === 'list' && (
        <ol className="mt-4 space-y-2.5">
          {listTrips.length === 0 && (
            <p className="text-sm text-text-faint">{t('property.trips.empty_day')}</p>
          )}
          {listTrips.map((trip) => (
            <li key={trip.id} className="border-l-2 border-primary/40 pl-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                {formatTripWindow(trip.start, trip.end)}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-text">{trip.title}</p>
            </li>
          ))}
        </ol>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div aria-hidden="true" className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${formId}-heading`}
            className="relative w-full max-h-[90vh] overflow-y-auto rounded-t-lg border border-border bg-surface shadow-xl sm:mx-4 sm:max-w-lg sm:rounded-lg"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-4 py-4 sm:px-6">
              <h2 id={`${formId}-heading`} className="text-lg font-semibold text-text">{t('property.trips.add_title')}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-text-faint hover:text-text-muted" aria-label={t('property.trips.cancel')}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-6">
              {formError && (
                <p className="rounded-md border border-notification bg-notification-soft px-3 py-2 text-sm text-notification">
                  {formError}
                </p>
              )}
              <div>
                <label htmlFor={`${formId}-title`} className="mb-1 block text-sm font-medium text-text">
                  {t('property.trips.title_label')} *
                </label>
                <input
                  id={`${formId}-title`}
                  ref={dialogTitleRef}
                  type="text"
                  required
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t('property.trips.title_placeholder')}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${formId}-start`} className="mb-1 block text-sm font-medium text-text">
                    {t('property.trips.start')} *
                  </label>
                  <input
                    id={`${formId}-start`}
                    type="date"
                    required
                    value={form.start_date}
                    onChange={(event) => setForm({
                      ...form,
                      start_date: event.target.value,
                      end_date: event.target.value > form.end_date ? event.target.value : form.end_date,
                    })}
                    className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor={`${formId}-end`} className="mb-1 block text-sm font-medium text-text">
                    {t('property.trips.end')}
                  </label>
                  <input
                    id={`${formId}-end`}
                    type="date"
                    required
                    value={form.end_date}
                    min={form.start_date}
                    onChange={(event) => setForm({ ...form, end_date: event.target.value })}
                    className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              {locations.length > 0 && (
                <div>
                  <label htmlFor={`${formId}-location`} className="mb-1 block text-sm font-medium text-text">
                    {t('property.trips.location')}
                  </label>
                  <select
                    id={`${formId}-location`}
                    value={form.location_id}
                    onChange={(event) => setForm({ ...form, location_id: event.target.value })}
                    className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {locations.length > 1 && <option value="">{t('property.trips.select_property')}</option>}
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor={`${formId}-notes`} className="mb-1 block text-sm font-medium text-text">
                  {t('property.trips.notes')}
                </label>
                <textarea
                  id={`${formId}-notes`}
                  rows={3}
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                  className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t('property.trips.notes_placeholder')}
                />
              </div>
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-md border border-border px-4 py-2 text-sm text-text-muted hover:bg-surface-strong"
                >
                  {t('property.trips.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={create.isPending}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
                >
                  {t('property.trips.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TripDetailRow({
  trip,
  plannedLabel,
  completedLabel,
  scheduledLabel,
  onSiteLabel,
  onRemove,
  removeLabel,
}: {
  trip: CalendarTrip;
  plannedLabel: string;
  completedLabel: string;
  scheduledLabel: string;
  onSiteLabel: string;
  onRemove?: () => void;
  removeLabel: string;
}) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-text">{trip.title}</p>
        <p className="text-xs text-text-faint">
          {formatTripWindow(trip.start, trip.end)}
          {' · '}
          {sourceLabel(trip, plannedLabel, completedLabel, scheduledLabel, onSiteLabel)}
          {trip.locationName ? ` · ${trip.locationName}` : ''}
        </p>
        {trip.notes && <p className="mt-1 text-xs text-text-muted">{trip.notes}</p>}
      </div>
      {onRemove && (
        <button type="button" onClick={onRemove} className="text-xs text-text-faint hover:text-notification">
          {removeLabel}
        </button>
      )}
    </li>
  );
}

function WeekdayHeader() {
  return (
    <div className="grid grid-cols-7 gap-px">
      {WEEKDAY_LABELS.map((label) => (
        <div key={label} className="px-1 py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-text-faint">
          {label}
        </div>
      ))}
    </div>
  );
}

function MonthGrid({
  year,
  month,
  trips,
  selectedDate,
  today,
  compact,
  onSelect,
  onAddFuture,
}: {
  year: number;
  month: number;
  trips: CalendarTrip[];
  selectedDate: string;
  today: string;
  compact: boolean;
  onSelect: (iso: string) => void;
  onAddFuture: (iso: string) => void;
}) {
  const cells = monthCells(year, month);
  const maxChips = compact ? 1 : 3;
  return (
    <div className={compact ? '' : 'mt-3'}>
      <WeekdayHeader />
      <div className="grid grid-cols-7 gap-px rounded-md bg-border">
        {cells.map((date) => {
          const iso = toISODate(date);
          const inMonth = date.getMonth() === month;
          const dayTrips = tripsOnDate(trips, iso);
          const extra = Math.max(0, dayTrips.length - maxChips);
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelect(iso)}
              onDoubleClick={() => iso >= today && onAddFuture(iso)}
              aria-pressed={iso === selectedDate}
              aria-current={iso === today ? 'date' : undefined}
              className={`min-h-[2.75rem] ${compact ? 'sm:min-h-[4.5rem]' : 'sm:min-h-[6.5rem]'} bg-surface p-1 text-left hover:bg-surface-strong ${
                iso === selectedDate ? 'ring-1 ring-inset ring-primary' : ''
              } ${iso === today ? 'bg-primary/5' : ''} ${inMonth ? '' : 'opacity-40'}`}
            >
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                iso === today ? 'bg-primary text-white' : 'text-text-muted'
              }`}>
                {date.getDate()}
              </span>
              {/* A month cell is ~44px wide on a phone, too narrow for a title.
                  Below sm each visit is a dot and the day panel underneath
                  names them; from sm up the chips return. */}
              <span className="mt-1 flex gap-0.5 sm:hidden" aria-hidden="true">
                {dayTrips.slice(0, 3).map((trip) => (
                  <span key={trip.id} className={`h-1.5 w-1.5 rounded-full ${dotClass(trip)}`} />
                ))}
              </span>
              {dayTrips.length > 0 && (
                <span className="sr-only">
                  {dayTrips.length === 1 ? dayTrips[0].title : `${dayTrips.length}`}
                </span>
              )}
              <span className="mt-0.5 hidden space-y-0.5 sm:block">
                {dayTrips.slice(0, maxChips).map((trip) => (
                  <span
                    key={trip.id}
                    title={trip.title}
                    className={`block truncate rounded px-1 py-0.5 text-[10px] leading-tight ${chipClass(trip)}`}
                  >
                    {trip.title}
                  </span>
                ))}
                {extra > 0 && (
                  <span className="block px-1 text-[10px] text-text-faint">+{extra}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({
  anchor,
  trips,
  selectedDate,
  today,
  onSelect,
  onAddFuture,
}: {
  anchor: Date;
  trips: CalendarTrip[];
  selectedDate: string;
  today: string;
  onSelect: (iso: string) => void;
  onAddFuture: (iso: string) => void;
}) {
  const days = weekCells(anchor);
  return (
    <div className="mt-3">
      <WeekdayHeader />
      <div className="grid grid-cols-1 gap-px rounded-md bg-border sm:grid-cols-7">
        {days.map((date) => {
          const iso = toISODate(date);
          const dayTrips = tripsOnDate(trips, iso);
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelect(iso)}
              onDoubleClick={() => iso >= today && onAddFuture(iso)}
              className={`min-h-[8rem] bg-surface p-2 text-left hover:bg-surface-strong ${
                iso === selectedDate ? 'ring-1 ring-inset ring-primary' : ''
              } ${iso === today ? 'bg-primary/5' : ''}`}
            >
              <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                iso === today ? 'bg-primary text-white' : 'text-text'
              }`}>
                {date.getDate()}
              </span>
              <span className="mt-2 block space-y-1">
                {dayTrips.map((trip) => (
                  <span
                    key={trip.id}
                    title={trip.title}
                    className={`block truncate rounded px-1.5 py-1 text-[11px] leading-tight ${chipClass(trip)}`}
                  >
                    {trip.title}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
